import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
    LogOut,
    Loader2,
    ArrowLeft,
    User,
    MapPin,
    Package,
    Plus,
    Trash2,
    Home,
    CheckCircle2,
} from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { toast } from "sonner";
import {
    ORDER_STATUS_LABELS,
    ORDER_STATUS_STYLES,
    formatDateTime,
    formatPrice,
} from "../lib/orderStatus";

const TABS = [
    { k: "orders", label: "Encomendas", icon: Package },
    { k: "profile", label: "Dados pessoais", icon: User },
    { k: "addresses", label: "Moradas", icon: MapPin },
];

export default function CustomerAccount() {
    const { user, loading, logout, apiAuth, refreshMe } = useCustomerAuth();
    const [tab, setTab] = useState("orders");
    const [orders, setOrders] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (tab !== "orders") return;
        setBusy(true);
        apiAuth("/auth/orders")
            .then((r) => setOrders(r.data || []))
            .catch(() => toast.error("Erro a carregar encomendas"))
            .finally(() => setBusy(false));
    }, [user, tab, apiAuth]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }
    if (!user) return <Navigate to="/conta/entrar" replace />;

    return (
        <div className="min-h-screen bg-brand-cream" data-testid="customer-account">
            {/* Top bar */}
            <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-brand-green/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-2" data-testid="customer-logo-link">
                            <img src="/brand/logo-main.png" alt="Sabor Líquido" className="h-9 w-auto" />
                        </Link>
                        <span className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-brand-orange-soft px-3 py-1 text-xs font-bold uppercase tracking-widest text-brand-orange">
                            Minha conta
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/"
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-4 py-2 text-xs font-semibold transition-colors"
                            data-testid="customer-back-site"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Ir para o site
                        </Link>
                        <button
                            type="button"
                            onClick={logout}
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink hover:bg-red-500 text-white px-4 py-2 text-xs font-semibold transition-colors"
                            data-testid="customer-logout"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sair
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">
                    Olá, {user?.name?.split(" ")[0] || "Cliente"}
                </div>
                <h1 className="mt-1 font-display font-medium text-3xl md:text-4xl text-brand-ink">
                    A tua conta
                </h1>

                {/* Tabs */}
                <nav className="mt-8 flex gap-2 flex-wrap" aria-label="Tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.k;
                        return (
                            <button
                                key={t.k}
                                type="button"
                                onClick={() => setTab(t.k)}
                                data-testid={`customer-tab-${t.k}`}
                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all ${
                                    active
                                        ? "bg-brand-green text-white shadow-pop"
                                        : "bg-white text-brand-ink hover:bg-brand-green-soft"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                {t.label}
                            </button>
                        );
                    })}
                </nav>

                <div className="mt-6">
                    {tab === "orders" && <OrdersTab orders={orders} busy={busy} />}
                    {tab === "profile" && <ProfileTab user={user} apiAuth={apiAuth} refreshMe={refreshMe} />}
                    {tab === "addresses" && <AddressesTab user={user} apiAuth={apiAuth} refreshMe={refreshMe} />}
                </div>
            </main>
        </div>
    );
}

// ---------- ORDERS ----------
function OrdersTab({ orders, busy }) {
    if (busy) {
        return (
            <div className="p-16 flex justify-center bg-white rounded-2xl shadow-soft">
                <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
            </div>
        );
    }
    if (!orders.length) {
        return (
            <div className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-12 text-center">
                <Package className="h-10 w-10 text-brand-green/40 mx-auto" />
                <p className="mt-4 text-sm text-brand-ink-soft">Ainda não tens encomendas.</p>
                <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-5 py-2.5 text-xs font-semibold transition-colors"
                >
                    Começar a comprar
                </Link>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            {orders.map((o) => (
                <article
                    key={o.id}
                    data-testid={`order-card-${o.id}`}
                    className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-5 md:p-6"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display font-medium text-lg text-brand-ink">
                                    #{o.order_number || o.id.slice(0, 8)}
                                </span>
                                <span
                                    className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2.5 py-1 ${
                                        ORDER_STATUS_STYLES[o.order_status] || "bg-brand-cream text-brand-ink"
                                    }`}
                                >
                                    {ORDER_STATUS_LABELS[o.order_status] || o.order_status}
                                </span>
                            </div>
                            <div className="mt-1 text-xs text-brand-ink-soft">
                                {formatDateTime(o.created_at)}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-display font-medium text-xl text-brand-ink">
                                {formatPrice(o.amount, o.currency)}
                            </div>
                            {o.tracking_number && (
                                <div className="mt-1 text-[11px] text-brand-green font-semibold">
                                    {o.carrier || "Envio"} · {o.tracking_number}
                                </div>
                            )}
                        </div>
                    </div>

                    {o.items_snapshot?.length > 0 && (
                        <ul className="mt-4 divide-y divide-brand-green/10">
                            {o.items_snapshot.map((it, i) => (
                                <li key={i} className="py-2 flex items-center justify-between text-sm">
                                    <span className="text-brand-ink">
                                        {it.name}{" "}
                                        <span className="text-brand-ink-soft">x{it.quantity}</span>
                                    </span>
                                    <span className="font-semibold text-brand-ink">
                                        {formatPrice(it.subtotal, o.currency)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {o.status_history?.length > 0 && (
                        <details className="mt-3">
                            <summary className="cursor-pointer text-xs font-semibold text-brand-green">
                                Ver histórico do estado
                            </summary>
                            <ol className="mt-3 space-y-2">
                                {o.status_history.map((h, i) => (
                                    <li key={i} className="flex items-start gap-2 text-xs text-brand-ink-soft">
                                        <CheckCircle2 className="h-3.5 w-3.5 text-brand-green mt-0.5" />
                                        <div>
                                            <span className="font-semibold text-brand-ink">
                                                {ORDER_STATUS_LABELS[h.status] || h.status}
                                            </span>{" "}
                                            · {formatDateTime(h.at)}
                                            {h.note && <div className="opacity-80">{h.note}</div>}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </details>
                    )}
                </article>
            ))}
        </div>
    );
}

// ---------- PROFILE ----------
function ProfileTab({ user, apiAuth, refreshMe }) {
    const [name, setName] = useState(user.name || "");
    const [phone, setPhone] = useState(user.phone || "");
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await apiAuth("/auth/me", {
                method: "PATCH",
                data: { name, phone: phone || null },
            });
            await refreshMe();
            toast.success("Dados atualizados");
        } catch {
            toast.error("Erro ao guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={save}
            data-testid="customer-profile-form"
            className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-6 md:p-8 max-w-xl"
        >
            <div className="space-y-4">
                <Labeled label="Nome">
                    <input
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="customer-profile-name"
                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                    />
                </Labeled>
                <Labeled label="Email">
                    <input
                        disabled
                        value={user.email}
                        className="w-full rounded-2xl border-2 border-brand-green/10 bg-brand-cream px-4 py-3 text-sm text-brand-ink-soft"
                    />
                </Labeled>
                <Labeled label="Telefone">
                    <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        data-testid="customer-profile-phone"
                        placeholder="+351 912 345 678"
                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                    />
                </Labeled>
            </div>
            <button
                type="submit"
                disabled={saving}
                data-testid="customer-profile-save"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-6 py-3 text-sm font-semibold transition-all disabled:opacity-60"
            >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Guardar alterações
            </button>
        </form>
    );
}

const Labeled = ({ label, children }) => (
    <div>
        <label className="text-xs font-bold uppercase tracking-widest text-brand-ink-soft mb-1.5 block">
            {label}
        </label>
        {children}
    </div>
);

// ---------- ADDRESSES ----------
function AddressesTab({ user, apiAuth, refreshMe }) {
    const addresses = useMemo(() => user.addresses || [], [user]);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(emptyAddress());
    const [busy, setBusy] = useState(false);

    const add = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await apiAuth("/auth/addresses", {
                method: "POST",
                data: {
                    full_name: form.full_name || null,
                    phone: form.phone || null,
                    line1: form.line1,
                    line2: form.line2 || null,
                    postal_code: form.postal_code,
                    city: form.city,
                    country: form.country || "Portugal",
                },
            });
            await refreshMe();
            setForm(emptyAddress());
            setShowAdd(false);
            toast.success("Morada adicionada");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao adicionar");
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Remover esta morada?")) return;
        try {
            await apiAuth(`/auth/addresses/${id}`, { method: "DELETE" });
            await refreshMe();
            toast.success("Morada removida");
        } catch {
            toast.error("Erro ao remover");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-brand-ink-soft">
                    {addresses.length === 0
                        ? "Ainda não tens moradas guardadas."
                        : `${addresses.length} morada(s) guardada(s).`}
                </p>
                <button
                    type="button"
                    onClick={() => setShowAdd((v) => !v)}
                    data-testid="customer-address-toggle-add"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 text-xs font-semibold transition-colors"
                >
                    <Plus className="h-3.5 w-3.5" /> Adicionar morada
                </button>
            </div>

            {showAdd && (
                <form
                    onSubmit={add}
                    data-testid="customer-address-form"
                    className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                    <Labeled label="Nome completo">
                        <Inp value={form.full_name} onChange={upd("full_name", setForm)} testid="addr-full_name" />
                    </Labeled>
                    <Labeled label="Telefone">
                        <Inp value={form.phone} onChange={upd("phone", setForm)} testid="addr-phone" />
                    </Labeled>
                    <div className="md:col-span-2">
                        <Labeled label="Morada (linha 1)">
                            <Inp required value={form.line1} onChange={upd("line1", setForm)} testid="addr-line1" />
                        </Labeled>
                    </div>
                    <div className="md:col-span-2">
                        <Labeled label="Morada (linha 2)">
                            <Inp value={form.line2} onChange={upd("line2", setForm)} testid="addr-line2" />
                        </Labeled>
                    </div>
                    <Labeled label="Código postal">
                        <Inp required value={form.postal_code} onChange={upd("postal_code", setForm)} testid="addr-postal_code" />
                    </Labeled>
                    <Labeled label="Cidade">
                        <Inp required value={form.city} onChange={upd("city", setForm)} testid="addr-city" />
                    </Labeled>
                    <Labeled label="País">
                        <Inp value={form.country} onChange={upd("country", setForm)} testid="addr-country" />
                    </Labeled>
                    <div className="md:col-span-2 flex gap-2">
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="customer-address-save"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-6 py-3 text-sm font-semibold transition-all disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Guardar morada
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowAdd(false)}
                            className="inline-flex items-center gap-2 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-6 py-3 text-sm font-semibold transition-all"
                        >
                            Cancelar
                        </button>
                    </div>
                </form>
            )}

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((a) => (
                    <li
                        key={a.id}
                        data-testid={`address-card-${a.id}`}
                        className="relative rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-5"
                    >
                        {a.is_default && (
                            <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full bg-brand-green text-white px-2 py-0.5">
                                <Home className="h-3 w-3" /> Por defeito
                            </span>
                        )}
                        {a.full_name && (
                            <div className="font-bold text-brand-ink">{a.full_name}</div>
                        )}
                        <div className="text-sm text-brand-ink">{a.line1}</div>
                        {a.line2 && <div className="text-sm text-brand-ink-soft">{a.line2}</div>}
                        <div className="text-sm text-brand-ink-soft">
                            {a.postal_code} {a.city}
                        </div>
                        <div className="text-sm text-brand-ink-soft">{a.country}</div>
                        {a.phone && (
                            <div className="mt-2 text-xs text-brand-ink-soft">📞 {a.phone}</div>
                        )}
                        <button
                            type="button"
                            onClick={() => del(a.id)}
                            data-testid={`address-delete-${a.id}`}
                            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 text-xs font-semibold transition-colors"
                        >
                            <Trash2 className="h-3 w-3" /> Remover
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

const Inp = ({ testid, ...props }) => (
    <input
        {...props}
        data-testid={testid}
        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
    />
);

const upd = (key, setter) => (e) => setter((f) => ({ ...f, [key]: e.target.value }));

const emptyAddress = () => ({
    full_name: "",
    phone: "",
    line1: "",
    line2: "",
    postal_code: "",
    city: "",
    country: "Portugal",
});
